/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api';

import Loader from '../common/Loader';
import TooltipButton from '../common/TooltipButton';
import VerficationRecordModal from './VerficationRecordModal';

import { formatDateAndTime } from '../../utils';
import { DEPLOYMENT_TIER } from '../../utils/appConfig';

import { useAlerts, useAuth } from '../context';

function VerificationRecords({ verificationStatuses, endUser, fetchEndUserData, redirect }) {
  const {
    handle,
    full_name,
    verification_history,
    member_verifications,
    entity_type,
    certification_status,
  } = endUser;

  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const { env } = useAuth();
  const navigate = useNavigate();

  const [rec, setRec] = useState({
    show: false,
    data: undefined,
    handle: handle,
    name: full_name,
  });
  const [loaded, setLoaded] = useState({
    verification_history: verification_history.length !== 0,
    member_verifications: !!member_verifications,
  });

  const [kycLevels, setKycLevels] = useState([]);
  const [isLoadingKycLevels, setIsLoadingKycLevels] = useState(!!kycLevels.length);

  const fetchKycLevels = async () => {
    try {
      const response = await adminApi.getKycLevels();
      if (response.success) {
        setKycLevels(response.kyc_levels);
        setIsLoadingKycLevels(false);
      } else {
        redirect();
      }
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
      redirect();
    }
  };

  const getVerficationHistory = async (type) => {
    setLoaded({ ...loaded, [type]: false });
    fetchEndUserData((response) => {
      setLoaded({ ...loaded, [type]: response[type] && response[type].length !== 0 });
    });
  };

  const submitRecord = async (values, type, id) => {
    if (values) {
      try {
        await adminApi.updateEndUser(handle, {
          verification_records: [
            id
              ? {
                  action: 'update',
                  uuid: id,
                  verification_status: values.verification_status,
                  comment: values.comment || null,
                }
              : {
                  action: 'add',
                  kyc_level: values.kyc_level,
                  comment: values.comment || null,
                },
          ],
        });
        setAlert({
          message: t(
            `admin.endUsers.records.new_evaluation.form.messages.${id ? 'update' : 'create'}`
          ),
          variant: 'success',
        });
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
      } finally {
        getVerficationHistory(type);
        setRec({
          ...rec,
          show: false,
          data: undefined,
          handle: handle,
          name: full_name,
        });
      }
    } else {
      setRec({
        ...rec,
        show: false,
        data: undefined,
        handle: handle,
        name: full_name,
      });
    }
  };

  const shouldCreateAlloyLink = (token) => {
    return token && typeof token === 'string' && token.trim().length > 0;
  };

  const shouldCreatePassportLink = (token) => {
    if (typeof token !== 'string') return false;
    return token && !Number.isNaN(token);
  };

  const formatLink = (record) => {
    if (record.verification_link) {
      return (
        <Button
          href={record.verification_link}
          target="_blank"
          variant="link"
          className="p-0 text-reset text-underline"
        >
          {t('admin.endUsers.records.verification_history.buttons.viewEval')}
        </Button>
      );
    }

    if (record.entity_token && shouldCreatePassportLink(record.entity_token)) {
      const customerProfileUrl = `/customers/detail/${record.entity_token}?tab=profile`;
      let portalBaseUrl = 'https://pm.prioritypassport.com';
      if (DEPLOYMENT_TIER === 'stage' || DEPLOYMENT_TIER === 'local' || env === 'sandbox') {
        portalBaseUrl = 'https://pm.sandbox.prioritypassport.com';
      }

      return (
        <Button
          href={portalBaseUrl + customerProfileUrl}
          target="_blank"
          variant="link"
          className="p-0 text-reset text-underline"
        >
          {t('admin.endUsers.records.verification_history.buttons.viewPassportCustomer')}
        </Button>
      );
    }

    if (record.parent_verification) {
      return null;
    }

    return <span className="text-info">{t('common:labels.na')}</span>;
  };

  useEffect(() => {
    setIsLoadingKycLevels(true);
    fetchKycLevels();
  }, []);

  if (isLoadingKycLevels) return <Loader />;

  return (
    <>
      {verification_history.length !== 0 && (
        <>
          <h4 className="mb-2">
            {t(`admin.endUsers.records.headers.${entity_type}.verification`)}
          </h4>
          <p className="text-info mb-2">
            {t(`admin.endUsers.records.verification_history.${entity_type}.description`)}
          </p>
          <p className="text-end mb-3">
            <Button
              variant="link"
              onClick={() => getVerficationHistory('verification_history')}
              className="text-reset text-lg p-0"
            >
              <i className="fas fa-sync-alt me-3 text-primary" />
              {t('common:buttons.refresh')}
            </Button>
          </p>
          <Card className="mb-2 border overflow-hidden">
            {!loaded.verification_history && <Loader />}
            <Table responsive>
              <thead>
                <tr>
                  <th className="border-end">
                    {t('admin.endUsers.records.verification_history.labels.date', {
                      type:
                        entity_type === 'business' ? t('common:kyb.label') : t('common:kyc.label'),
                    })}
                  </th>
                  <th className="border-end">
                    {t('admin.endUsers.records.verification_history.labels.level', {
                      type:
                        entity_type === 'business' ? t('common:kyb.label') : t('common:kyc.label'),
                    })}
                  </th>
                  <th className="border-end">
                    <div className="d-flex justify-content-between">
                      <div>{t('admin.endUsers.records.verification_history.labels.links')}</div>
                      <TooltipButton
                        placement="top"
                        variant="link"
                        text={t('admin.endUsers.records.verification_history.linksTooltip')}
                        className="no-underline p-0"
                      >
                        <i className="sila-icon info text-lg ms-5" />
                      </TooltipButton>
                    </div>
                  </th>
                  <th className="border-end">
                    {t('admin.endUsers.records.verification_history.labels.reasons')}
                  </th>
                  <th className="border-end">
                    {t('admin.endUsers.records.verification_history.labels.verification')}
                  </th>
                  {entity_type === 'business' && (
                    <th className="border-end">{t('common:kyb.certification_status')}</th>
                  )}
                  <th className="text-center">{t('common:labels.action')}</th>
                </tr>
              </thead>
              <tbody>
                {verification_history.map((record) => (
                  <tr key={record.verification_id}>
                    <td className="border-end">{formatDateAndTime(record.requested_at * 1000)}</td>
                    <td className="border-end">{record.kyc_level}</td>
                    <td className="border-end">
                      <div>{formatLink(record)}</div>
                      {record.parent_verification && (
                        <Button
                          variant="link"
                          className="p-0 text-reset text-underline mt-1"
                          onClick={() =>
                            navigate(
                              `/admin/end_user/${record.parent_verification.parent_user_handle}/records`
                            )
                          }
                        >
                          {t('admin.endUsers.records.verification_history.buttons.parentLink')}
                        </Button>
                      )}
                    </td>
                    <td className="border-end">
                      <div>
                        {record.reasons ? record.reasons.join(', ') : t('common:labels.na')}
                      </div>
                    </td>
                    <td className="border-end">
                      <Badge
                        pill
                        className={`badge-outline py-2 px-3 w-100 badge-${
                          record.verification_status.includes('passed')
                            ? 'success'
                            : record.verification_status.includes('failed')
                              ? 'warning'
                              : 'primary'
                        }`}
                      >
                        {
                          verificationStatuses.find(
                            (status) => status.name === record.verification_status
                          ).label
                        }
                      </Badge>
                    </td>
                    {entity_type === 'business' && (
                      <td className="border-end">{t(`common:kyb.${certification_status}`)}</td>
                    )}
                    <td className="d-flex justify-content-center">
                      {record.verification_id ? (
                        <Button
                          className="p-0 text-lg text-info hover"
                          onClick={() =>
                            setRec({
                              ...record,
                              show: true,
                              data: record,
                              name: full_name,
                              handle: handle,
                            })
                          }
                          variant="link"
                        >
                          <i className="fas fa-pen" />
                        </Button>
                      ) : (
                        <TooltipButton
                          placement="auto"
                          variant="link"
                          text={t('admin.endUsers.records.verification_history.messages.noId')}
                          className="p-0 cursor-pointer"
                        >
                          <i className="fas fa-exclamation-triangle text-warning text-lg" />
                        </TooltipButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
          <p className="text-end mb-5">
            <Button
              variant="link"
              className="p-0 text-underline"
              onClick={() =>
                setRec({
                  ...rec,
                  show: true,
                  handle: handle,
                  name: full_name,
                })
              }
            >
              {t('admin.endUsers.records.verification_history.buttons.addEval')}
            </Button>
          </p>
        </>
      )}

      {member_verifications && member_verifications.length !== 0 && (
        <>
          <h4 className="mb-2">
            {t(`admin.endUsers.records.headers.${entity_type}.member_verifications`)}
          </h4>
          <p className="text-info mb-2">
            {t(`admin.endUsers.records.member_verifications.description`)}
          </p>
          <p className="text-end mb-3">
            <Button
              variant="link"
              className="text-reset text-lg p-0"
              onClick={() => getVerficationHistory('member_verifications')}
            >
              <i className="fas fa-sync-alt me-3 text-primary" />
              {t('common:buttons.refresh')}
            </Button>
          </p>
          <Card className="mb-2 border position-relative overflow-hidden">
            {!loaded.member_verifications && <Loader />}
            <Table responsive>
              <thead>
                <tr>
                  <th className="border-end">{t('common:form.fields.name.label')}</th>
                  <th className="border-end">{t('common:kyb.roles.controlling_officer')}</th>
                  <th className="border-end">{t('common:kyb.roles.beneficial_owner')}</th>
                  <th className="border-end">{t('common:kyb.roles.admin')}</th>
                  <th className="border-end">
                    {t('admin.endUsers.records.verification_history.labels.verification')}
                  </th>
                  <th className="border-end">{t('common:kyb.certification_status')}</th>
                  <th>{t('common:labels.action')}</th>
                </tr>
              </thead>
              <tbody>
                {member_verifications.map((record) => (
                  <tr key={record.verification_id}>
                    <td className="border-end">
                      <Button
                        href={`/admin/end_user/${record.user_handle}/profile`}
                        variant="link"
                        className="text-reset text-underline p-0"
                      >{`${record.first_name} ${record.last_name}`}</Button>
                    </td>
                    <td className="border-end">
                      {record.role === 'controlling_officer' && (
                        <i className="fas fa-circle text-primary" />
                      )}
                    </td>
                    <td className="border-end">
                      {record.role === 'beneficial_owner' && (
                        <i className="fas fa-circle text-primary" />
                      )}
                    </td>
                    <td className="border-end">
                      {record.role === 'administrator' && (
                        <i className="fas fa-circle text-primary" />
                      )}
                    </td>
                    {record.entity_token && (
                      <td className="border-end">
                        {shouldCreateAlloyLink(record.entity_token) ? (
                          <Button
                            href={`https://app.alloy.co/entities/${record.entity_token}/evaluations/${record.evaluation_token}`}
                            target="_blank"
                            variant="link"
                            className="p-0 text-reset text-underline"
                          >
                            {t('admin.endUsers.records.verification_history.addNewEvalLink')}
                          </Button>
                        ) : (
                          <span className="text-info">{t('common:labels.na')}</span>
                        )}
                      </td>
                    )}
                    <td className="border-end">
                      <Badge
                        pill
                        className="badge-outline py-2 px-3 w-100"
                        variant={
                          record.verification_status.includes('passed')
                            ? 'success'
                            : record.verification_status.includes('failed')
                              ? 'warning'
                              : 'primary'
                        }
                      >
                        {
                          verificationStatuses.find(
                            (status) => status.name === record.verification_status
                          ).label
                        }
                      </Badge>
                    </td>
                    <td className="border-end">
                      {t(`common:kyb.${record.beneficial_owner_certification_status}`)}
                    </td>
                    <td>
                      {record.verification_id ? (
                        <Button
                          className="p-0 text-lg text-info hover"
                          onClick={() =>
                            setRec({
                              ...record,
                              show: true,
                              data: record,
                              handle: record.user_handle,
                              name: `${record.first_name} ${record.last_name}`,
                            })
                          }
                          variant="link"
                        >
                          <i className="fas fa-pen" />
                        </Button>
                      ) : (
                        <TooltipButton
                          placement="auto"
                          variant="link"
                          text={t('admin.endUsers.records.verification_history.messages.noId')}
                          className="p-0 cursor-pointer"
                        >
                          <i className="fas fa-exclamation-triangle text-warning text-lg" />
                        </TooltipButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      {kycLevels.length !== 0 && verificationStatuses.length !== 0 && (
        <VerficationRecordModal
          show={rec.show}
          onHide={submitRecord}
          data={rec.data}
          name={rec.name}
          kyc_levels={kycLevels}
          verification_statuses={verificationStatuses}
          flow={entity_type === 'business' ? 'kyb' : 'kyc'}
          handle={handle}
        />
      )}
    </>
  );
}

VerificationRecords.propTypes = {
  /**
   * The values of the form
   */
  // eslint-disable-next-line react/forbid-prop-types
  endUser: PropTypes.object.isRequired,
  /**
   * The values of the form
   */
  // eslint-disable-next-line react/forbid-prop-types
  verificationStatuses: PropTypes.array.isRequired,
  /**
   * Fetches and sets EndUser data
   */
  fetchEndUserData: PropTypes.func.isRequired,
  /**
   * A function whose call results in a redicrect
   */
  redirect: PropTypes.func.isRequired,
};

export default VerificationRecords;
